/**
 * LX-03 Framework branding (INV-CH-LX-03; kept).
 *
 * Events, weighted as the seed: BRAND_RE ×2.5, TROPE_RES ×2, capitalised concept coinages ×2,
 * GROWTH_RE ×1, per 1k prose words. All regexes run once per prose block (prefiltered).
 */
import {
	Tally,
	compiledEnglish,
	fmt,
	line,
	nativeMeasure,
	proseBlocks,
	quote,
	rateDef,
	wordExposure,
} from './a-shared.ts';
import { makeEvidence, rateChannel } from './framework.ts';
import type { ChannelImpl, Measurement, RunContext } from './types.ts';

const W_BRAND = 2.5;
const W_TROPE = 2;
const W_CONCEPT = 2;
const W_GROWTH = 1;

export const lx03Def = rateDef({
	id: 'LX-03',
	name: 'Framework branding',
	family: 'VOCAB',
	features: [
		{ id: 'brand', label: 'Branded framework' },
		{ id: 'trope', label: 'Guru trope' },
		{ id: 'concept', label: 'Capitalised concept coinage' },
		{ id: 'growth', label: 'Funnel-speak' },
	],
	oneWay: true,
	minExposure: 300,
	exposureUnit: 'words',
	lang: 'native',
	ramp: [
		[0, 6],
		[2, 25],
		[5, 45],
		[10, 64],
		[20, 82],
		[35, 94],
	],
	unitScale: 1000,
	unitLabel: 'weighted units per 1k words',
	phi0: 1.5,
	weight: 1.4,
	nd: 0.15,
	changes: [],
});

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const lex = compiledEnglish();
	const concept = lex.channels['ch-lx03'];
	const k = new Float64Array(doc.paragraphs.count);
	const e = wordExposure(doc);
	const ev = makeEvidence();
	const brand: string[] = [];
	const trope: string[] = [];
	const conceptHits: string[] = [];
	const growth = new Tally();
	let words = 0;
	let wsum = 0;
	for (const b of proseBlocks(doc)) {
		words += e[b.p] as number;
		const add = (s: number, en: number, f: number, w: number) => {
			k[b.p] = (k[b.p] as number) + w;
			wsum += w;
			ev.add(s, en, f, w);
		};
		lex.brand.scanBlock(b.text, b.base, (s, en, _i, m) => {
			add(s, en, 0, W_BRAND);
			brand.push(m[0]);
		});
		lex.trope.scanBlock(b.text, b.base, (s, en, _i, m) => {
			add(s, en, 1, W_TROPE);
			trope.push(m[0]);
		});
		concept.list.scanBlock(b.text, b.base, (s, en, _i, m) => {
			add(s, en, 2, W_CONCEPT);
			conceptHits.push(m[0]);
		});
		lex.growth.scanBlock(b.text, b.base, (s, en, _i, m) => {
			add(s, en, 3, W_GROWTH);
			growth.add(m[0].toLowerCase());
		});
	}
	const summary: string[] = [];
	const named: string[] = [];
	for (const m of brand) if (named.length < 12) named.push(line(quote(m), 'branded framework'));
	for (const m of trope) if (named.length < 12) named.push(line(quote(m), 'guru trope'));
	for (const m of conceptHits)
		if (named.length < 12) named.push(line(quote(m), 'capitalised concept coinage'));
	summary.push(...named);
	for (const [key, n] of growth.top())
		if (summary.length < 14) summary.push(line(key, `${n}× funnel-speak`));
	return {
		k,
		e,
		evidence: ev,
		summary,
		features: {
			weightedPerK: (wsum / Math.max(1, words)) * 1000,
			brandHits: brand.length,
			wsum: Number(fmt(wsum, 1)),
		},
	};
}

export const lx03: ChannelImpl = rateChannel(lx03Def, nativeMeasure(measure));
export { lx03Def as def, lx03 as impl };
